import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Layers } from 'lucide-react';
import toast from 'react-hot-toast';
import { releaseTypeApi } from '@/shared/services/releaseTypeApi';
import { getErrorMessage } from '@/shared/services/api';
import { ReleaseTypeDto } from '@/shared/types/api';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';

interface FormState {
  name: string;
  description: string;
  isActive: boolean;
}

const emptyForm: FormState = { name: '', description: '', isActive: true };

/**
 * Admin management of the "Release Type" metadata vocabulary (SC-300). These values are what a
 * curator can assign to a feed item and filter the feed by.
 */
export function AdminReleaseTypes() {
  const [releaseTypes, setReleaseTypes] = useState<ReleaseTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editing, setEditing] = useState<ReleaseTypeDto | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      // Include inactive so an admin can see and re-activate them.
      setReleaseTypes(await releaseTypeApi.list(true));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load release types'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (rt: ReleaseTypeDto) => {
    setEditing(rt);
    setForm({ name: rt.name, description: rt.description ?? '', isActive: rt.isActive });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const onSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await releaseTypeApi.update(editing.id, form.name.trim(), form.description || null, form.isActive);
        toast.success('Release type updated');
      } else {
        await releaseTypeApi.create(form.name.trim(), form.description || null);
        toast.success('Release type created');
      }
      closeModal();
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save release type'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (rt: ReleaseTypeDto) => {
    if (!window.confirm(`Delete release type "${rt.name}"? Items already tagged keep their value.`)) return;
    setBusyId(rt.id);
    try {
      await releaseTypeApi.remove(rt.id);
      toast.success('Release type deleted');
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete release type'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          {releaseTypes.length} release type{releaseTypes.length === 1 ? '' : 's'}
        </p>
        <button onClick={openCreate} className="btn-primary gap-2">
          <Plus className="h-4 w-4" />
          Add release type
        </button>
      </div>

      {releaseTypes.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Layers}
            title="No release types"
            description="Add the values a curator can assign to feed items."
          />
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-left">
              <thead>
                <tr className="bg-muted/50">
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                  <th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {releaseTypes.map((rt) => (
                  <tr key={rt.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{rt.name}</div>
                      {rt.description && (
                        <div className="max-w-md truncate text-sm text-muted-foreground" title={rt.description}>
                          {rt.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn(rt.isActive ? 'badge-success' : 'badge-neutral')}>
                        {rt.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1" aria-busy={busyId === rt.id}>
                        {busyId === rt.id && <Spinner className="mr-1 h-4 w-4 text-muted-foreground" />}
                        <button
                          onClick={() => openEdit(rt)}
                          disabled={busyId === rt.id}
                          className="btn-icon btn-ghost h-8 w-8"
                          title="Edit release type"
                          aria-label={`Edit release type ${rt.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDelete(rt)}
                          disabled={busyId === rt.id}
                          className="btn-icon btn-ghost h-8 w-8 text-danger hover:text-danger"
                          title="Delete release type"
                          aria-label={`Delete release type ${rt.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs animate-fade-in">
          <div className="surface-overlay w-full max-w-lg animate-slide-up p-6 shadow-modal">
            <h2 className="mb-4 text-xl font-bold text-foreground">
              {editing ? 'Edit release type' : 'Add release type'}
            </h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="rt-name" className="label">Name *</label>
                <input
                  id="rt-name"
                  className="input mt-1.5"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Automatic Release - Config Required"
                />
              </div>
              <div>
                <label htmlFor="rt-description" className="label">Description</label>
                <textarea
                  id="rt-description"
                  className="input mt-1.5 resize-none"
                  rows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What this release type means"
                />
              </div>
              {editing && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active (selectable on items and in the filter)
                </label>
              )}
            </div>
            <div className="mt-6 flex gap-2">
              <button type="button" onClick={closeModal} className="btn-outline flex-1">Cancel</button>
              <button type="button" onClick={onSave} disabled={saving} className="btn-primary flex-1">
                {saving && <Spinner className="text-primary-foreground" />}
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add release type'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
